import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const API_URL = window.location.origin;
const CLOUDINARY_UPLOAD_PRESET = "trianf_unsigned"; 
const CLOUDINARY_CLOUD_NAME = "dxzsqhaz8";
//const API_URL = process.env.VITE_APP_API_URL || 'https://trianf.com';

// 1. INCORPORATED COUNTRIES MAP DATA
const COUNTRY_GRADE_SUBJECTS_MAP = {
'United States': {
    'Pre-School Pre-K': ['Creative Arts', 'Literacy and Language Arts', 'Mathematics', 'Music and Movement', 'Practical Life/Self-care', 'Science', 'Social-Emotinal Development', 'Social Studies'],
    'Elementary School K1-K5': ['Fine Arts', 'ELA', 'Spanish', 'Library', 'Mathematics', 'Music', 'Physical Education', 'Religion', 'Science', 'Social Studies', 'Technology'],
    'Middle School K6-K8': ['Math', 'Science', 'World History'],
    'High School K9-K12': ['Math', 'Science', 'World History'],
    'Standard Tests': ['Math', 'Science', 'World History'],
    'College and University': ['Math', 'Science', 'World History'],
  },
  'United Kingdom': {
    'Year 6': ['placeholder', 'Science', 'English'],
    'Year 7': ['Maths', 'History', 'Geography'],
  },
  'Canada': {
    'Grade 6': ['Math', 'French', 'Social Studies'],
    'Grade 7': ['Math', 'Science', 'English'],
  },
 'Uganda': {
    'Kindergarten': ['Language and Literacy', 'Mathematical Concepts', 'Environmental Interaction: Science/Social Studies', 'Personal and Social Development','Physical Development: Health/Motor skills','Creative and Expressive Arts'],
    'Primary': ['English', 'Mathematics', 'Science', 'Social Studies'],
    'Secondary: O Level': ['Agriculture', 'Arabic', 'Biology', 'Chemistry', 'Chinese', 'Christian Religious Education', 'English Language', 'Entrepreneurship', 'Fine Arts', 'French', 'General Science', 'German', 'History and Political Education', 'Information Communication and Technology', 'Islamic Religious Education', 'Kiswahili', 'Latin', 'Literature in English', 'Luganda', 'Lusoga', 'Mathematics', 'Nutrition and Food Technology', 'Physical Education', 'Performing Arts', 'Physics', 'Technology and Design'],
    'Secondary: A Level': ['Agriculture', 'Arabic', 'Biology', 'Chemistry', 'Chinese', 'Christian Religious Education', 'English Language', 'Entrepreneurship', 'Fine Arts', 'French', 'General Science', 'German', 'History and Political Education', 'Information Communication and Technology', 'Islamic Religious Education', 'Kiswahili', 'Latin', 'Literature in English', 'Luganda', 'Lusoga', 'Mathematics', 'Nutrition and Food Technology', 'Physical Education', 'Performing Arts', 'Physics', 'Technology and Design'],
    'Mature Entry': ['English Language', 'Mathematics', 'Aptitude test/Specialized Paper', 'General Paper'],
    'University': ['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public'],
  }

};

const SEN_DATA_MAP = {
  'Cognitive and Learning Differences': {
    'Specific Learning Disabilities (SLD)': [
      'Dyslexia (Reading & Spelling)',
      'Dyscalculia (Math & Numbers)',
      'Dysgraphia (Writing & Fine Motor)',
      'Executive Function Coaching'
    ],
    'Intellectual Disabilities': [
      'Functional Literacy',
      'Life Skills & Independence',
      'Modified Curriculum Pacing',
      'Alternative Communication (AAC)'
    ]
  },
  'Communication and Interaction Needs': {
    'Speech or Language Impairments (SLI)': [
      'Articulation & Phonology Support',
      'Expressive Language Building',
      'Receptive Language Comprehension',
      'Stuttering & Fluency Accommodations'
    ],
    'Autism Spectrum Disorder (ASD)': [
      'Sensory Decompression Routines',
      'Social Clue & Interaction Coaching',
      'Visual Schedules & Structured Routines',
      'Non-verbal Communication Support'
    ]
  },
  'Neurobehavioral and Executive Functioning': {
    'Attention-Deficit/Hyperactivity Disorder (ADHD)': [
      'Focus & Attention Strategies',
      'Impulse Control Support',
      'Time Management & Organization',
      'Kinesthetic / Active Learning'
    ],
    'Emotional & Mental Health Conditions': [
      'Anxiety Coping Mechanisms',
      'Behavioral Intervention Plans (BIP)',
      'De-escalation Strategies',
      'Confidence & Mindset Building'
    ]
  },
  'Sensory and Physical Needs': {
    'Multiple Disabilities': [
      'Severe Dual-Sensory Support',
      'Combined Cognitive & Physical Accommodations',
      'Assistive Technology Integration',
      'Individualized Health Plan Alignment'
    ],
    'Orthopedic / Physical Impairments': [
      'Wheelchair Accessible Lesson Planning',
      'Fine/Gross Motor Skill Adaptations',
      'Fatigue Management & Pacing'
    ],
    'Visual Impairments & Blindness': [
      'Braille Reading/Writing Support',
      'Screen Reader Compatibility Optimization',
      'Tactile Learning Materials'
    ],
    'Hearing Impairments & Deafness': [
      'Sign Language Instruction (ASL/BSL)',
      'Total Communication Approach',
      'Visual Phonology & Lip-reading Support'
    ]
  }
};

const Tutor1 = () => {
  const sigPad = useRef(null);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [userTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Local dropdown tracking states for Section 4
const [selectedArea, setSelectedArea] = useState('');
const [selectedNeed, setSelectedNeed] = useState('');
const [selectedSkills, setSelectedSkills] = useState([]);
   
  const initialState = {first_name: "", middle_name: "", last_name: "", 
    email: "", password: "", confirmPassword: "", // Added for registration
    phone: "",
    avatar_url: "", h_address: "", h_city: "", zip_code: "", h_state: "",
    country: "", highest_edn: "", teach_qfn: "", bio: "", examiner: "",
    above_18: "", authorized: "", resume_cv: "", hourly_rate: "", currency: "USD",
    subject1: "", subject2: "", sen_area: [], grade_levels: "",
    crim_back_chk: "", fullname: "", role: "tutor", signature: ""}

  const [formData, setFormData] = useState(initialState);

  // --- DERIVED MEMOIZED LISTS FOR DEPENDENT DROPDOWNS ---
  const availableGrades = useMemo(() => {
    if (!formData.country || !COUNTRY_GRADE_SUBJECTS_MAP[formData.country]) return [];
    return Object.keys(COUNTRY_GRADE_SUBJECTS_MAP[formData.country]);
  }, [formData.country]);

  const availableSubjects = useMemo(() => {
    if (!formData.country || !formData.grade_levels || !COUNTRY_GRADE_SUBJECTS_MAP[formData.country]) return [];
    return COUNTRY_GRADE_SUBJECTS_MAP[formData.country][formData.grade_levels] || [];
  }, [formData.country, formData.grade_levels]);

  // Memoized lookups for the cascading dropdowns
const availableNeeds = useMemo(() => {
  if (!selectedArea || !SEN_DATA_MAP[selectedArea]) return [];
  return Object.keys(SEN_DATA_MAP[selectedArea]);
}, [selectedArea]);

const availableSkills = useMemo(() => {
  if (!selectedArea || !selectedNeed || !SEN_DATA_MAP[selectedArea][selectedNeed]) return [];
  return SEN_DATA_MAP[selectedArea][selectedNeed];
}, [selectedArea, selectedNeed]);

// Toggle specific checkbox skills
const handleSkillToggle = (skill) => {
  if (selectedSkills.includes(skill)) {
    setSelectedSkills(selectedSkills.filter(s => s !== skill));
  } else {
    setSelectedSkills([...selectedSkills, skill]);
  }
};

// Append the new qualification object straight into the master formData state
const handleAddSenSpecialization = () => {
  if (!selectedArea || !selectedNeed || selectedSkills.length === 0) return;

  const newSpecialization = {
    id: Date.now(),
    area: selectedArea,
    need: selectedNeed,
    skills: selectedSkills
  };

  setFormData(prev => ({
    ...prev,
    sen_area: [...prev.sen_area, newSpecialization] // Pushes directly into master state
  }));

  // Clear local layout fields for next selection
  setSelectedArea('');
  setSelectedNeed('');
  setSelectedSkills([]);
};

// Remove a specialized block from the profile array
const handleRemoveSenSpec = (id) => {
  setFormData(prev => ({
    ...prev,
    sen_area: prev.sen_area.filter(item => item.id !== id)
  }));
};

  // --- EFFECT TO RESTORE SIGNATURE CANVAS DATA ---
  useEffect(() => {
    if (step === 5 && formData.signature && sigPad.current && sigPad.current.isEmpty()) {
      sigPad.current.fromDataURL(formData.signature);
    }
  }, [step, formData.signature]);

// --- FILE UPLOAD ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setLoading(true);
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
        method: "POST",
        body: data
      });
      const fileData = await res.json();
      setFormData({ ...formData, [e.target.name]: fileData.secure_url });
      setStatus("✅ File uploaded successfully");
    } catch (err) {
      setStatus("❌ File upload failed");
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = () => {
    localStorage.setItem('tutor_draft', JSON.stringify(formData));
    setStatus("💾 Draft saved locally!");
    setTimeout(() => setStatus(''), 2000);
  };
  // ------------------------------


// Function to upload the canvas drawing to Cloudinary
  const uploadSignature = async () => {
   let signatureImage = formData.signature;
  
  if (sigPad.current && !sigPad.current.isEmpty()) {
    signatureImage = sigPad.current.getTrimmedCanvas().toToDataURL('image/png');
  }

  if (!signatureImage) {
    console.log("No signature data found.");
    return null;
  }

    const uploadData = new FormData();
    uploadData.append("file", signatureImage);
    uploadData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: "POST", body: uploadData }
      );
      const data = await response.json();
      return data.secure_url; // This is the URL we save to the DB
    } catch (error) {
      console.error("Cloudinary Signature Upload Error:", error);
      return null;
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

// --- CUSTOM DROPDOWN HANDLERS TO HANDLE CASCADING RESETS ---
  const handleCountryDropdownChange = (e) => {
    setFormData({
      ...formData,
      country: e.target.value,
      grade_levels: "", // Clear old selection downstream
      subject1: "", 
      subject2: ""
    });
  };

  const handleGradeDropdownChange = (e) => {
    setFormData({
      ...formData,
      grade_levels: e.target.value,
      subject1: "", // Clear old selection downstream
      subject2: ""
    });
  };

const nextStep = () => {
    // Basic validation before moving from Step 1
    if (step === 1) {
      if (formData.password !== formData.confirmPassword) {
        return setStatus("❌ Passwords do not match");
      }
      if (formData.password.length < 6) {
        return setStatus("❌ Password must be at least 6 characters");
      }
    }
    setStatus('');
    setStep(step + 1);
  };
  
  const prevStep = () => setStep(step - 1);

  // CLEAN AND SIMPLE ATOMIC SUBMISSION
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('Submitting profile...');
    
    // 1. Upload signature first
    const signatureUrl = await uploadSignature();
    
    // 2. Prepare the combined payload
    const fullPayload = { 
      ...formData, 
      sen_area: JSON.stringify(formData.sen_area), // Converts the array to a clean JSON string for the DB
      signature: signatureUrl || formData.signature,
      firstName: formData.first_name, 
      middleName: formData.middle_name,
      lastName: formData.last_name,
      timezone: userTimeZone,
      is_draft: false,
      is_complete: true,
      approved: false, 
      admin_feedback: null
    };

    try {
      // 3. Single call to the unified endpoint
      const response = await fetch(`${API_URL}/api/register-tutor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullPayload),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Registration failed");
      
      setStatus('✅ Success! Redirecting to login...');
      localStorage.removeItem('tutor_draft'); // Clean draft on success
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

// Helper to render Review Labels
  const ReviewItem = ({ label, value }) => (
    <div className="border-b py-1">
      <span className="text-gray-500 text-xs uppercase font-bold">{label}:</span>
      <p className="text-sm">{value || 'N/A'}</p>
    </div>
  );  

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-6">
    {/*<div className="max-w-2xl mx-auto mt-10 mb-10 bg-white rounded-lg shadow-md t-24"> */}   
      <h3 className="text-2xl font-bold mt-10 ">Tutor registration and Setup</h3>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2 mb-6 rounded-full overflow-hidden">
        <div className="bg-blue-600 h-full transition-all duration-300" style={{ width: `${(step / 6) * 100}%` }} ></div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* STEP 1: PERSONAL */}
        {step === 1 && (
        <div className="space-y-4 animate-in fade-in">       
          <h3 className='font-bold border-b pb-2 text-blue-600'>Section 1: Account & Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" name="first_name" placeholder="First Name *" value={formData.first_name} onChange={handleChange} required className="p-2 border rounded" />
               <input type="text" name="middle_name" placeholder="Middle Name" value={formData.middle_name} onChange={handleChange} className="p-2 border rounded" />
              <input type="text" name="last_name" placeholder="Last Name *" value={formData.last_name} onChange={handleChange} required className="p-2 border rounded" />
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-gray-600 block mb-1">Profile Photo</label>
                <input 
                  type="file" 
                  name="avatar_url" 
                  onChange={handleFileUpload} 
                  accept="image/*" 
                  className="w-full p-1 border rounded text-sm file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700"
                />
                {formData.avatar_url && <p className="text-[10px] text-green-600 mt-1">Photo Ready: {formData.avatar_url.substring(0, 40)}...</p>}
              </div>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-3 rounded border border-blue-100">
              <input type="email" name="email" placeholder="Email Address *" value={formData.email} onChange={handleChange} required className="p-2 border rounded md:col-span-2" />
               <input type="password" name="password" placeholder="Create Password *" value={formData.password} onChange={handleChange} required className="p-2 border rounded" />
                <input type="password" name="confirmPassword" placeholder="Confirm Password *" value={formData.confirmPassword} onChange={handleChange} required className="p-2 border rounded" />
                <p className="text-[10px] text-gray-500 md:col-span-2">Password will be used for your TRiANf login.</p>
              </div>             
              <input type="number" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} className="p-2 border rounded" />
               </div>
          </div>
        )}
        
      
{/* STEP 2: ADDRESS */}
        {step === 2 && (
          <div className="space-y-4">
        <h3 className='font-bold border-b pb-2'>Section 2: Address</h3>
        <input type="text" name="h_address" placeholder="Home Address" value={formData.h_address} onChange={handleChange} required className="w-full p-2 border rounded" />
        <div className="grid grid-cols-2 gap-4">
          <input type="text" name="h_city" placeholder="City" value={formData.h_city} onChange={handleChange} required className="p-2 border rounded" />
          <input type="text" name="h_state" placeholder="Your state" value={formData.h_state} onChange={handleChange} className="w-full p-2 border rounded" />
         {/* Country Turned into a Safe Dropdown Selection mapped from config data */}
              <select 
                name="country" 
                value={formData.country} 
                onChange={handleCountryDropdownChange} 
                required 
                className="p-2 border rounded bg-white"
              >
                <option value="">-- Select Country * --</option>
                {Object.keys(COUNTRY_GRADE_SUBJECTS_MAP).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
        </div>
        <p className="text-xs text-gray-500">Auto-detected Timezone: {userTimeZone}</p>
</div>
        )}
        {/* STEP 3: PROFESSIONAL INFO */}
        {step === 3 && (
          <div className="space-y-4">
        <h3 className='font-bold border-b pb-2'>Section 3: Professional Info</h3>
        <textarea name="bio" placeholder="Brief Biography (max 600 chars)" value={formData.bio} onChange={handleChange} maxLength="600" className="w-full p-2 border rounded h-24" />        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input type="text" name="highest_edn" placeholder="Highest Education" value={formData.highest_edn} onChange={handleChange} required className="p-2 border rounded" />
        <input type="text" name="teach_qfn" placeholder="State Teaching Qualification" value={formData.teach_qfn} onChange={handleChange} className="w-full p-2 border rounded" />
        <div className="flex flex-col">
        <label className="text-xs font-bold text-gray-600 mb-1">Hourly Rate & Currency</label>
        <div className="flex gap-2">
          <select 
            name="currency" 
            value={formData.currency} 
            onChange={handleChange}
            className="p-2 border rounded bg-gray-50 font-bold"
          >
            {/* Major Global Currencies */}
  <option value="USD">USD ($)</option>
  <option value="EUR">EUR (€)</option>
  <option value="GBP">GBP (£)</option>
  <option value="CAD">CAD ($)</option>
  <option value="AUD">AUD ($)</option>
  <option value="JPY">JPY (¥)</option>
  <option value="CNY">CNY (¥)</option>

  {/* African Currencies */}
  <option value="UGX">UGX (USh)</option>
  <option value="ZAR">ZAR (R)</option>
  <option value="NGN">NGN (₦)</option>
  <option value="KES">KES (KSh)</option>
  <option value="GHS">GHS (GH₵)</option>
  <option value="EGP">EGP (E£)</option>

  {/* Asian & Middle Eastern Currencies */}
  <option value="INR">INR (₹)</option>
  <option value="AED">AED (د.إ)</option>
  <option value="SAR">SAR (﷼)</option>
  <option value="SGD">SGD ($)</option>
  <option value="HKD">HKD ($)</option>
  <option value="PKR">PKR (₨)</option>

  {/* Other Regions */}
  <option value="BRL">BRL (R$)</option>
  <option value="MXN">MXN ($)</option>
  <option value="NZD">NZD ($)</option>
  <option value="CHF">CHF (CHf)</option>
          </select>
          <input 
            type="number" 
            name="hourly_rate" 
            placeholder="Rate" 
            value={formData.hourly_rate} 
            onChange={handleChange} 
            required 
            className="p-2 border rounded flex-1" 
          />
        </div>
      </div>
        
        <input type="text" name="examiner" placeholder="Are you an Examiner" value={formData.examiner} onChange={handleChange} required className="w-full p-2 border rounded" />
        <input type="text" name="above_18" placeholder="Are you Above 18 Years" value={formData.above_18} onChange={handleChange} required className="w-full p-2 border rounded" />           
        <input type="text" name="authorized" placeholder="Are you authorized to work in the country" value={formData.authorized} onChange={handleChange} required className="w-full p-2 border rounded" />           
        <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Upload Resume/CV (PDF/Doc)</label>
              <input 
                type="file" 
                name="resume_cv" 
                onChange={handleFileUpload} 
                accept=".pdf,.doc,.docx"
                className="w-full p-1 border rounded text-sm file:bg-blue-50 file:text-blue-700"
              />
              {formData.resume_cv && <p className="text-[10px] text-green-600 mt-1">Document Ready!</p>}
            </div>
        </div>
        </div>
        )}  
        {/* STEP 4: TUTORING SPECIFICS (DYNAMIC CONDITIONAL UI) */}
        {step === 4 && (   
        <div className="space-y-4">   
        <h3 className='font-bold border-b pb-2'>Section 4: Tutoring Specifics</h3>
        {/* If they haven't selected a country in Step 2, prompt them */}
            {!formData.country ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded text-sm">
                ⚠️ Please go back to <strong>Section 2</strong> and choose a country to unlock grade and subject choices.
              </div>
            ) : (
              <>
                {/* 1. Grade Levels dynamic dropdown instead of open string input */}
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">Grade Level * ({formData.country})</label>
                  <select 
                    name="grade_levels" 
                    value={formData.grade_levels} 
                    onChange={handleGradeDropdownChange} 
                    required
                    className="w-full p-2 border rounded bg-white"
                  >
                    <option value="">-- Choose Grade Level --</option>
                    {availableGrades.map((grade) => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>

                {/* 2. Major Subject Selection */}
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">Major Subject You Handle *</label>
                  <select 
                    name="subject1" 
                    value={formData.subject1} 
                    onChange={handleChange} 
                    disabled={!formData.grade_levels}
                    required
                    className="w-full p-2 border rounded bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">-- Choose Major Subject --</option>
                    {availableSubjects.map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>            

                {/* 3. Minor Subject Selection */}
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">Minor Subject You Handle</label>
                  <select 
                    name="subject2" 
                    value={formData.subject2} 
                    onChange={handleChange} 
                    disabled={!formData.grade_levels}
                    className="w-full p-2 border rounded bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">-- Choose Minor Subject (Optional) --</option>
                    {availableSubjects.map((sub) => (
                      // Avoid allowing them to pick the exact same subject for major and minor
                      sub !== formData.subject1 && <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                  </div>
       {/* --- INTEGRATED SEN MULTI-SELECT SYSTEM --- */}
        <div className="pt-4 border-t border-gray-200 space-y-3">
          <label className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Special Education Needs (SEN) Specialization</label>
          
          <div className="p-3 bg-gray-50 border rounded-lg space-y-3">
            <select
              value={selectedArea}
              onChange={(e) => { setSelectedArea(e.target.value); setSelectedNeed(''); setSelectedSkills([]); }}
              className="w-full p-2 text-sm border rounded bg-white"
            >
              <option value="">-- Choose SEN Category Area --</option>
              {Object.keys(SEN_DATA_MAP).map((area) => <option key={area} value={area}>{area}</option>)}
            </select>

            <select
              value={selectedNeed}
              onChange={(e) => { setSelectedNeed(e.target.value); setSelectedSkills([]); }}
              disabled={!selectedArea}
              className="w-full p-2 text-sm border rounded bg-white disabled:bg-gray-100"
            >
              <option value="">-- Choose Primary Condition --</option>
              {availableNeeds.map((need) => <option key={need} value={need}>{need}</option>)}
            </select>

            {availableSkills.length > 0 && (
              <div className="bg-white p-2 border rounded max-h-40 overflow-y-auto space-y-1">
                <p className="text-[10px] font-bold text-gray-500 uppercase px-1">Select competencies:</p>
                {availableSkills.map((skill) => (
                  <label key={skill} className="flex items-center gap-2 p-1 hover:bg-gray-50 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={selectedSkills.includes(skill)} onChange={() => handleSkillToggle(skill)} className="rounded text-blue-600" />
                    <span>{skill}</span>
                  </label>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleAddSenSpecialization}
              disabled={!selectedNeed || selectedSkills.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-bold py-1.5 px-3 rounded text-xs transition"
            >
              + Add Specialization to Profile
            </button>
          </div>

          {/* Active List of Added Specializations */}
          {formData.sen_area.length > 0 && (
            <div className="space-y-2">
              {formData.sen_area.map((item) => (
                <div key={item.id} className="relative p-2.5 bg-blue-50/60 border border-blue-100 rounded-lg text-xs">
                  <button type="button" onClick={() => handleRemoveSenSpec(item.id)} className="absolute top-1 right-2 text-gray-400 hover:text-red-500 font-bold">✕</button>
                  <p className="font-bold text-gray-800">{item.need} <span className="text-[9px] font-normal text-gray-500">({item.area})</span></p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {item.skills.map(s => <span key={s} className="bg-white border border-blue-100 text-gray-600 px-1.5 py-0.5 rounded-full text-[10px]">✓ {s}</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>                     
         </>        
       )}
    </div>        
  )}
        {/* STEP 5: VERIFICATION */}
        {step === 5 && (
          <div className="space-y-4">
        <h3 className='font-bold border-b pb-2'>Section 5: Verification & Signature</h3>        
         <input type="text" name="crim_back_chk" placeholder="Do you consent to criminal background check if required?" value={formData.crim_back_chk} onChange={handleChange} className="w-full p-2 border rounded" />           
        <p className="text-sm italic">"I certify that my answers are true and complete"</p>
    
    <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 p-2">
      <p className="text-[10px] font-bold text-gray-500 mb-1 uppercase">Draw your signature below:</p>
      <SignatureCanvas 
        ref={sigPad} 
        penColor='black'
        canvasProps={{
          className: 'sigCanvas w-full h-40 bg-white border rounded cursor-crosshair'
        }} 
        onEnd={() => {
    if (sigPad.current) {
      const base64Data = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
      setFormData(prev => ({ ...prev, signature: base64Data }));
    }
  }}
      />
      <div className="flex justify-end mt-2">
        <button 
          type="button" 
          onClick={() => sigPad.current.clear()} 
          className="text-xs font-bold text-red-500 hover:text-red-700 underline"
        >
          Clear Signature
        </button>
      </div>
    </div>

    <input 
      type="text" 
      name="fullname" 
      placeholder="Type Full Name to Verify" 
      value={formData.fullname} 
      onChange={handleChange} 
      required 
      className="w-full p-2 border rounded" 
    />
  </div>
)}
        {/* STEP 6: CONFIRMATION (PREVIEW) */}
        {step === 6 && (
          <div className="space-y-6 bg-gray-50 p-6 rounded-lg border border-blue-100 animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className='font-bold text-blue-600 text-lg'>Step 6: Review Your Information</h3>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">Summary</span>
            </div>

            <div className="space-y-4">
              {/* Personal Section */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <h4 className="text-sm font-bold text-gray-800">Personal Information</h4>
                  <button type="button" onClick={() => setStep(1)} className="text-xs text-blue-600 hover:underline font-semibold">Edit</button>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded border">
                  <ReviewItem label="Name" value={`${formData.first_name} ${formData.last_name}`} />
                  <ReviewItem label="Email" value={formData.email} />
                </div>
              </div>

              {/* Location Section */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <h4 className="text-sm font-bold text-gray-800">Address & Location</h4>
                  <button type="button" onClick={() => setStep(2)} className="text-xs text-blue-600 hover:underline font-semibold">Edit</button>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded border">
                  <ReviewItem label="City" value={formData.h_city} />
                  <ReviewItem label="Country" value={formData.country} />
                </div>
              </div>

              {/* Professional Section */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <h4 className="text-sm font-bold text-gray-800">Professional Details</h4>
                  <button type="button" onClick={() => setStep(3)} className="text-xs text-blue-600 hover:underline font-semibold">Edit</button>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded border">
                  <ReviewItem label="Hourly Rate" value={`${formData.currency} ${formData.hourly_rate}/hr`} />
                  <ReviewItem label="Education" value={formData.highest_edn} />
                </div>
              </div>

              {/* Tutoring Section */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <h4 className="text-sm font-bold text-gray-800">Tutoring & SEN Specifics</h4>
                  <button type="button" onClick={() => setStep(4)} className="text-xs text-blue-600 hover:underline font-semibold">Edit</button>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded border">
                  <ReviewItem label="Grade Levels" value={formData.grade_levels} />
                  <ReviewItem label="Major / Minor" value={`${formData.subject1} / ${formData.subject2 || 'None'}`} />
                  <div className="pt-2 border-t mt-1 col-span-2">
      <span className="text-gray-500 text-xs uppercase font-bold">Registered SEN Areas:</span>
      {formData.sen_area.length === 0 ? (
        <p className="text-xs text-gray-400 italic mt-0.5">None Selected</p>
      ) : (
        <ul className="list-disc pl-4 mt-1 space-y-1 text-xs text-gray-700">
          {formData.sen_area.map(item => (
            <li key={item.id}>
              <strong>{item.need}:</strong> {item.skills.join(', ')}
            </li>
          ))}
        </ul>
      )}
    </div>
    </div>
  </div>
  </div>

            <div className="p-3 bg-yellow-50 border border-yellow-100 rounded">
                <p className="text-[10px] text-yellow-800 leading-tight">
                    <strong>Final Check:</strong> By submitting, you confirm that the information above is accurate and that you consent to our terms of service.
                </p>
            </div>
          </div>
        )}

        {/* NAVIGATION BUTTONS */}
        <div className="flex justify-between items-center pt-4">
          <div className="flex gap-3">
          {step > 1 && (
            <button 
              type="button" 
              onClick={prevStep} 
              className="bg-gray-500 text-white px-6 py-2 rounded font-bold hover:bg-gray-600 transition"
            >
              Previous
            </button>
          )}
{step < 6 && (
          <button 
      type="button" 
      onClick={saveDraft} 
      className="text-blue-600 border border-blue-600 px-4 py-2 rounded font-bold hover:bg-blue-50"
    >
      Save Draft
      </button>
)}
      </div>
          
          {step < 6 ? (
            <button 
              type="button" 
              onClick={nextStep} 
              className="ml-auto bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700 transition"
            >
              Next
            </button>
          ) : (
        <button type="submit" className="w-full bg-blue-600 text-white p-3 rounded font-bold hover:bg-blue-700 transition">
         {loading ? 'Submitting...' : 'Submit Profile'}
        </button>
          )}
        </div>
      </form>   
      
      {status && <p className="mt-4 text-center text-sm font-bold text-blue-600">{status}</p>}
    </div>
  );
};

export default Tutor1;
